import type { ReactNode } from 'react';
import { CheckIcon } from '@/components/ui/Icons';

/**
 * A large radio presented as a card — used for the role choice and for picking
 * where the one-time code is delivered. Rendered as a real radio input so
 * keyboard and screen-reader behaviour comes for free.
 */
export function OptionCard({
  name,
  value,
  checked,
  onSelect,
  icon,
  title,
  description,
}: {
  name: string;
  value: string;
  checked: boolean;
  onSelect: (value: string) => void;
  icon?: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <label
      className={`group relative flex cursor-pointer items-start gap-3.5 rounded-2xl border p-4 transition duration-200 ease-out ${
        checked
          ? 'border-brand-500 bg-brand-50/70 shadow-soft ring-1 ring-brand-500/20'
          : 'border-line bg-white hover:border-brand-300 hover:bg-brand-50/40'
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onSelect(value)}
        className="sr-only"
      />

      {icon && (
        <span
          className={`flex size-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
            checked ? 'bg-brand-gradient text-white' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {icon}
        </span>
      )}

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-ink">{title}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{description}</span>
      </span>

      <span
        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border transition ${
          checked ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white'
        }`}
      >
        {checked && <CheckIcon className="size-3" />}
      </span>
    </label>
  );
}
